import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { OutlookEmail } from '@maximizer/outlook/shared/domain';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

type ComposeState = {
  isLoading: boolean;
  inMaximizer: OutlookEmail[];
  notInMaximizer: OutlookEmail[];
  selectedEmailsToSave: OutlookEmail[];
  saveOnlyEntries: OutlookEmail[];
  lastEmailSignature: string | null;
  composeId: string | null;
  heartbeatActive: boolean;
};

const initialState: ComposeState = {
  isLoading: false,
  inMaximizer: [],
  notInMaximizer: [],
  selectedEmailsToSave: [],
  saveOnlyEntries: [],
  lastEmailSignature: null,
  composeId: null,
  heartbeatActive: false,
};

export const ComposeStore = signalStore(
  withDevtools('compose'),
  withState({
    ...initialState,
  }),
  withMethods((store) => ({
    setLoading(isLoading: boolean) {
      patchState(store, () => ({ isLoading }));
    },
    setComposeId(composeId: string | null) {
      patchState(store, () => ({ composeId }));
    },
    setHeartbeatActive(heartbeatActive: boolean) {
      patchState(store, () => ({ heartbeatActive }));
    },
    updateInMaximizer(inMaximizer: OutlookEmail[]) {
      patchState(store, () => ({ inMaximizer }));
    },
    updateNotInMaximizer(notInMaximizer: OutlookEmail[]) {
      patchState(store, () => ({ notInMaximizer }));
    },
    updateSelectedEmailsToSave(selectedEmailsToSave: OutlookEmail[]) {
      patchState(store, () => ({ selectedEmailsToSave }));
    },
    updateSaveOnlyEntries(saveOnlyEntries: OutlookEmail[]) {
      patchState(store, () => ({ saveOnlyEntries }));
    },
    addEmailToSaveOnly(email: OutlookEmail) {
      const currentEntries = store.saveOnlyEntries();
      const exists = currentEntries.some(
        (entry) => entry.localId === email.localId,
      );

      if (!exists) {
        const updatedEntries = [...currentEntries, email];
        patchState(store, () => ({ saveOnlyEntries: updatedEntries }));
      }
    },
    removeEmailFromSaveOnly(email: OutlookEmail) {
      const currentEntries = store.saveOnlyEntries();
      const updatedEntries = currentEntries.filter(
        (entry) => entry.localId !== email.localId,
      );
      patchState(store, () => ({ saveOnlyEntries: updatedEntries }));
    },
    removeEmailFromInMaximizer(email: OutlookEmail) {
      const updatedEntries = store
        .inMaximizer()
        .filter((entry) => entry.localId !== email.localId);
      patchState(store, () => ({ inMaximizer: updatedEntries }));
    },
    addEmailToSelected(email: OutlookEmail) {
      if (!email.id) {
        return;
      }

      const currentSelected = store.selectedEmailsToSave();
      const exists = currentSelected.some(
        (existing) => existing.id === email.id,
      );

      if (!exists) {
        patchState(store, () => ({
          selectedEmailsToSave: [...currentSelected, email],
        }));
      }
    },
    removeEmailFromSelected(email: OutlookEmail) {
      if (!email.id) {
        return;
      }

      const currentSelected = store.selectedEmailsToSave();
      const updatedSelected = currentSelected.filter(
        (existing) => existing.id !== email.id,
      );

      patchState(store, () => ({
        selectedEmailsToSave: updatedSelected,
      }));
    },
    initializeFromSessionStorage() {
      const savedSelectedEmails = sessionStorage.getItem(
        'selectedEmailsToSave',
      );
      if (savedSelectedEmails) {
        const selectedEmailsToSave = JSON.parse(
          savedSelectedEmails,
        ) as OutlookEmail[];
        patchState(store, () => ({ selectedEmailsToSave }));
      }
    },
    saveCheckboxStateInSession(checkboxStates: { [localId: string]: boolean }) {
      const composeId = store.composeId();
      if (!composeId) return;

      sessionStorage.setItem(
        `checkboxStates_${composeId}`,
        JSON.stringify(checkboxStates),
      );
    },
    getCheckboxStateFromSession(): { [localId: string]: boolean } | null {
      const composeId = store.composeId();
      if (!composeId) return null;

      const savedStates = sessionStorage.getItem(`checkboxStates_${composeId}`);
      if (savedStates) {
        return JSON.parse(savedStates) as { [localId: string]: boolean };
      }
      return null;
    },
    removeCheckboxStateFromSession(localId: string) {
      const composeId = store.composeId();
      if (!composeId) return;

      const savedStates = this.getCheckboxStateFromSession();
      if (savedStates?.hasOwnProperty(localId)) {
        delete savedStates[localId];
        sessionStorage.setItem(
          `checkboxStates_${composeId}`,
          JSON.stringify(savedStates),
        );
      }
    },
  })),
);

export type ComposeStore = InstanceType<typeof ComposeStore>;
